/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Recommendation Video Component
 *
 * @file RecommendationVideoComponent.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import ChannelsVideoRecommendationCard from '../common/ChannelsVideoComponent/ChannelsVideoCard';
import ChannelsPracticeRecommendationCard from '../common/ChannelsPracticeCard/ChannelsPracticeCard';
import {
  ContentContainer,
  WidgetSection,
  WidgetSectionTwo,
  WidgetSectionSingle
} from './RemediationComponentStyle';

const RemediationComponent = ({
  asset,
  practiceAsset,
  showOnly,
  channelId,
  env,
  getUserCallback,
  onSeeInChannelsLinkClick,
  containerProps
}) => {
  const videoAssets = React.useMemo(() => {
    if (!asset) return [];

    return Array.isArray(asset) ? asset : [asset];
  }, [asset]);

  const practiceAssets = React.useMemo(() => {
    if (!practiceAsset) return [];

    return Array.isArray(practiceAsset) ? practiceAsset : [practiceAsset];
  }, [practiceAsset]);

  const trackingGlobalData = React.useMemo(() => ({
    location_in_app: 'video-player'
  }), []);

  const shouldShowVideo = videoAssets.length > 0 && (!showOnly || showOnly === 'video');
  const shouldShowPractice = practiceAssets.length > 0 && (!showOnly || showOnly === 'problem');

  const totalWidgets = videoAssets.length + practiceAssets.length;

  if (!shouldShowVideo && !shouldShowPractice) {
    return null;
  }

  const getWidgetComponent = () => {
    if (totalWidgets === 1) return WidgetSectionSingle;
    if (totalWidgets === 2) return WidgetSectionTwo;

    return WidgetSection;
  };

  const WidgetBox = getWidgetComponent();

  return (
    <ContentContainer
      {...containerProps}
    >
      {shouldShowVideo && videoAssets.map((videoAsset, index) => (
        <WidgetBox key={videoAsset?.id || `video-${index}`}>
          <ChannelsVideoRecommendationCard
            asset={videoAsset}
            env={env}
            user={getUserCallback()}
            channelId={channelId}
            onSeeInChannelsLinkClick={onSeeInChannelsLinkClick}
          />
        </WidgetBox>
      ))}

      {shouldShowPractice && practiceAssets.map((practiceAssetItem, index) => (
        <WidgetBox key={practiceAssetItem?.id || `practice-${index}`}>
          <ChannelsPracticeRecommendationCard
            asset={practiceAssetItem}
            channelId={channelId}
            env={env}
            user={getUserCallback()}
            trackingGlobalData={{ ...trackingGlobalData, location_in_app: 'asset' }}
            onAnswerInStudyPrep={onSeeInChannelsLinkClick}
          />
        </WidgetBox>
      ))}
    </ContentContainer>
  );
};

RemediationComponent.propTypes = {
  asset: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  practiceAsset: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  showOnly: PropTypes.oneOf(['video', 'problem']),
  channelId: PropTypes.string.isRequired,
  env: PropTypes.string,
  getUserCallback: PropTypes.func,
  onSeeInChannelsLinkClick: PropTypes.func,
  containerProps: PropTypes.object
};

RemediationComponent.defaultProps = {
  asset: undefined,
  practiceAsset: undefined,
  showOnly: undefined,
  env: 'stg',
  getUserCallback: () => ({ userId: 'demo', token: 'demo' }),
  onSeeInChannelsLinkClick: () => {},
  containerProps: {}
};

export default RemediationComponent;
